// maths.portll.net — the FIVE closed dataset shapes.
//
// This is the single source of truth shared by: the parent data-entry UI, the
// "Your Data" question generators, the print-sheet recording grids, and the
// content pipeline. Keeping the set CLOSED is the load-bearing decision — a new
// task is authored CONTENT, never new engine code. Firestore stores each dataset's
// `data` as a MAP; arrays inside hold scalars or {maps}. Bivariate pairs are
// [{x,y}] maps, NEVER [[x,y]] nested arrays (Firestore rejects directly-nested
// arrays — the rival-ghosts lesson).

/** @typedef {'categorical_tally'|'trial_outcomes'|'numerical_list'|'two_sample'|'bivariate_pairs'} DatasetKind */

export const DATASET_KINDS = /** @type {DatasetKind[]} */ ([
  "categorical_tally",
  "trial_outcomes",
  "numerical_list",
  "two_sample",
  "bivariate_pairs",
]);

// Shapes shipped in the public beta (F–6 data loop). two_sample + bivariate_pairs
// are authored/stored-valid but their generators land post-beta.
export const BETA_KINDS = /** @type {DatasetKind[]} */ ([
  "categorical_tally",
  "trial_outcomes",
  "numerical_list",
]);

export const isBetaKind = (kind) => BETA_KINDS.includes(kind);

// Minimum observations before the generators can build a decent "Your Data" pack.
// Below this we show a friendly "collect a few more" fallback instead of thin questions.
export const MIN_OBSERVATIONS = {
  categorical_tally: 8,
  trial_outcomes: 10,
  numerical_list: 5,
  two_sample: 6, // per group
  bivariate_pairs: 8,
};

const isFiniteNumber = (n) => typeof n === "number" && Number.isFinite(n);
const isNonEmptyString = (s) => typeof s === "string" && s.trim().length > 0;

/**
 * Validate a dataset's `data` payload against its kind.
 * @param {DatasetKind} kind
 * @param {any} data
 * @returns {{ ok: boolean, errors: string[], observations: number }}
 */
export function validateDataset(kind, data) {
  const errors = [];
  let observations = 0;
  if (!DATASET_KINDS.includes(kind)) {
    return { ok: false, errors: [`unknown kind "${kind}"`], observations: 0 };
  }
  if (data == null || typeof data !== "object" || Array.isArray(data)) {
    return { ok: false, errors: ["data must be an object"], observations: 0 };
  }

  switch (kind) {
    case "categorical_tally": {
      const cats = data.categories;
      if (!Array.isArray(cats) || cats.length < 2) errors.push("need at least 2 categories");
      else if (cats.length > 8) errors.push("at most 8 categories");
      else {
        for (const c of cats) {
          if (!c || !isNonEmptyString(c.label)) errors.push("each category needs a label");
          if (!Number.isInteger(c?.count) || c.count < 0 || c.count > 999) errors.push(`count for "${c?.label}" must be 0–999`);
        }
        observations = cats.reduce((s, c) => s + (Number(c?.count) || 0), 0);
      }
      break;
    }
    case "trial_outcomes": {
      const outs = data.outcomes;
      if (!Array.isArray(outs) || outs.length < 2) errors.push("need at least 2 outcomes");
      else {
        for (const o of outs) {
          if (!o || !isNonEmptyString(o.label)) errors.push("each outcome needs a label");
          if (!Number.isInteger(o?.count) || o.count < 0) errors.push(`count for "${o?.label}" must be a non-negative integer`);
        }
        observations = outs.reduce((s, o) => s + (Number(o?.count) || 0), 0);
      }
      if (data.trials != null && (!Number.isInteger(data.trials) || data.trials < 0)) errors.push("trials must be a non-negative integer");
      if (data.expectedModel != null && !isNonEmptyString(data.expectedModel)) errors.push("expectedModel must be a string when present");
      break;
    }
    case "numerical_list": {
      const vals = data.values;
      if (!Array.isArray(vals) || vals.length < 1) errors.push("need at least 1 value");
      else if (vals.length > 50) errors.push("at most 50 values");
      else if (!vals.every(isFiniteNumber)) errors.push("all values must be finite numbers");
      if (!isNonEmptyString(data.unitLabel)) errors.push("unitLabel is required");
      observations = Array.isArray(vals) ? vals.length : 0;
      break;
    }
    case "two_sample": {
      for (const key of ["groupA", "groupB"]) {
        const g = data[key];
        if (!g || !isNonEmptyString(g.label)) errors.push(`${key} needs a label`);
        if (!Array.isArray(g?.values) || g.values.length < 1 || !g.values.every(isFiniteNumber)) errors.push(`${key} needs a list of numbers`);
      }
      observations = Math.min(
        Array.isArray(data.groupA?.values) ? data.groupA.values.length : 0,
        Array.isArray(data.groupB?.values) ? data.groupB.values.length : 0
      );
      break;
    }
    case "bivariate_pairs": {
      const pairs = data.pairs;
      if (!Array.isArray(pairs) || pairs.length < 1) errors.push("need at least 1 (x,y) pair");
      else if (pairs.length > 40) errors.push("at most 40 pairs");
      else if (!pairs.every((p) => p && isFiniteNumber(p.x) && isFiniteNumber(p.y))) errors.push("each pair must be {x, y} with finite numbers (never [x,y])");
      if (!isNonEmptyString(data.xLabel) || !isNonEmptyString(data.yLabel)) errors.push("xLabel and yLabel are required");
      observations = Array.isArray(pairs) ? pairs.length : 0;
      break;
    }
  }

  return { ok: errors.length === 0, errors, observations };
}

/** True when there is enough data for the generators to build a real question pack. */
export function hasEnoughData(kind, data) {
  const { ok, observations } = validateDataset(kind, data);
  return ok && observations >= (MIN_OBSERVATIONS[kind] ?? 1);
}

/** An empty, schema-correct payload for a fresh entry form. */
export function emptyDataset(kind, spec = {}) {
  switch (kind) {
    case "categorical_tally":
      return { categories: (spec.suggestedCategories || ["", ""]).map((label) => ({ label, count: 0 })), unitLabel: spec.unitLabel || "" };
    case "trial_outcomes":
      return { trials: 0, outcomes: (spec.outcomes || ["", ""]).map((label) => ({ label, count: 0 })), expectedModel: spec.expectedModel ?? null };
    case "numerical_list":
      return { values: [], unitLabel: spec.unitLabel || "" };
    case "two_sample":
      return { groupA: { label: spec.groupALabel || "A", values: [] }, groupB: { label: spec.groupBLabel || "B", values: [] } };
    case "bivariate_pairs":
      return { pairs: [], xLabel: spec.xLabel || "x", yLabel: spec.yLabel || "y", xUnit: spec.xUnit || null, yUnit: spec.yUnit || null };
    default:
      return {};
  }
}
